/**
 * @file: PlanGroupHeader.tsx
 *
 * Header for one order's group of maintenance-plan details: plate + vehicle
 * line, spec line, and the order context (mileage / order no / time / service
 * agent) with an inline link to the source service record. The detail rows
 * below carry the same denormalized columns, so the group renders this once
 * and the per-item bodies omit the order block (mirrors the feed reminder, but
 * order info hoisted to the header).
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { LabelRow } from '@euler/app/flows/feed/components/message-body/common/primitives';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MaintenancePlanDetailListItem } from '@euler/model/task-detail';
import { formatDateTime } from '@euler/utils/formatters';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const PlanGroupHeader: FC<{ item: MaintenancePlanDetailListItem }> =
  memo(({ item }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useAppNavigation();

    const vehicleDisplay = item.vehicleName || item.vehicleModel;
    // 8-char Chinese plates are new-energy (NEV); 7 chars are fuel.
    const isNewEnergyPlate = item.licensePlateNo.length === 8;
    const plateColor = isNewEnergyPlate ? '#40c779' : '#1f80ff';
    const age = getVehicleAgeByVin(item.vin);
    const yearParts: string[] = [];
    if (item.vehicleModelYear) yearParts.push(`${item.vehicleModelYear} 款`);
    if (age > 0) yearParts.push(`车龄 ${age} 年`);
    const specParts = [
      item.vehicleDisplacement,
      item.vehicleFuelType,
      item.vehicleTrimLevel,
    ].filter((s): s is string => !!s);

    const onViewRecord = useCallback(() => {
      if (item.serviceRecordId) {
        navigation.push('ServiceRecord', { id: item.serviceRecordId });
      } else {
        navigation.push('Task', { taskNo: item.taskNo });
      }
    }, [navigation, item.serviceRecordId, item.taskNo]);

    return (
      <View
        css={`
          background-color: #eef4fb;
          border-top-width: ${rpx(8)}px;
          border-top-color: #dce7f4;
          border-bottom-width: 1px;
          border-bottom-color: #cbdcef;
          padding: ${rpx(12)}px ${rpx(16)}px;
        `}
      >
        <View
          css={`
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
          `}
        >
          <View
            css={`
              padding: ${rpx(1)}px ${rpx(6)}px;
              border-radius: ${rpx(4)}px;
              border-width: 1px;
              border-color: ${plateColor};
            `}
          >
            <Typography.Text
              size={rpx(13)}
              color={plateColor}
              medium
              letterSpacing={1}
            >
              {item.licensePlateNo}
            </Typography.Text>
          </View>
          <Typography.Text ml={rpx(8)} size={rpx(13)} color="#374151" medium>
            {vehicleDisplay}
          </Typography.Text>
          <View style={{ marginLeft: 'auto' }}>
            <TouchableOpacity
              onPress={onViewRecord}
              activeOpacity={0.7}
              hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
            >
              <Typography.Text size={rpx(12)} color="#1f80ff">
                {item.serviceRecordId ? '服务记录 →' : '服务中 →'}
              </Typography.Text>
            </TouchableOpacity>
          </View>
        </View>
        {yearParts.length > 0 || specParts.length > 0 ? (
          <Typography.Text mt={rpx(4)} size={rpx(12)} color="#6b7280">
            {[...yearParts, ...specParts].join(' · ')}
          </Typography.Text>
        ) : null}
        {item.currentMileage ? (
          <LabelRow label="进店里程" value={`${item.currentMileage} km`} />
        ) : null}
        <LabelRow label="进店单号" value={item.orderNo} />
        {item.orderedAt ? (
          <LabelRow label="进店时间" value={formatDateTime(item.orderedAt)} />
        ) : null}
        {item.finishedAt ? (
          <LabelRow label="完成时间" value={formatDateTime(item.finishedAt)} />
        ) : null}
        {item.sourceSaName ? (
          <LabelRow label="服务顾问" value={item.sourceSaName} />
        ) : null}
      </View>
    );
  });
